import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateCampaignModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cashbackRate: 5,
    rewardCap: 50,
    budget: '',
    targetAudience: '',
    category: ''
  });

  const categoryOptions = [
    { value: 'fashion', label: 'Fashion & Apparel' },
    { value: 'beauty', label: 'Beauty & Cosmetics' },
    { value: 'tech', label: 'Technology & Electronics' },
    { value: 'fitness', label: 'Fitness & Wellness' },
    { value: 'food', label: 'Food & Beverage' },
    { value: 'home', label: 'Home & Living' },
    { value: 'travel', label: 'Travel & Lifestyle' }
  ];

  const audienceOptions = [
    { value: 'micro', label: 'Micro Influencers (1K-10K)' },
    { value: 'mid', label: 'Mid-tier Influencers (10K-100K)' },
    { value: 'macro', label: 'Macro Influencers (100K-1M)' },
    { value: 'mega', label: 'Mega Influencers (1M+)' },
    { value: 'all', label: 'All Influencers' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onCreate(formData);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-custom">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Create New Campaign</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Set up your influencer marketing campaign
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
          <Input
            label="Campaign Name"
            type="text"
            placeholder="e.g., Summer Fashion Collection 2026"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Campaign Description
            </label>
            <textarea
              className="w-full px-4 py-3 rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              rows="4"
              placeholder="Describe your campaign goals and requirements..."
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Category"
              placeholder="Select category"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleChange('category', value)}
              required
            />

            <Select
              label="Target Audience"
              placeholder="Select audience"
              options={audienceOptions}
              value={formData?.targetAudience}
              onChange={(value) => handleChange('targetAudience', value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Cashback Rate: <span className="text-primary data-text">{formData?.cashbackRate}%</span>
            </label>
            <input
              type="range"
              min="1"
              max="20"
              step="0.5"
              value={formData?.cashbackRate}
              onChange={(e) => handleChange('cashbackRate', parseFloat(e?.target?.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1%</span>
              <span>20%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reward Cap per Transaction: <span className="text-primary data-text">${formData?.rewardCap}</span>
            </label>
            <input
              type="range"
              min="10"
              max="500"
              step="10"
              value={formData?.rewardCap}
              onChange={(e) => handleChange('rewardCap', parseInt(e?.target?.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>$10</span>
              <span>$500</span>
            </div>
          </div>

          <Input
            label="Total Campaign Budget"
            type="number"
            placeholder="e.g., 10000"
            value={formData?.budget}
            onChange={(e) => handleChange('budget', e?.target?.value)}
            required
            description="Total amount allocated for this campaign"
          />

          <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Campaign Estimate</p>
                <p className="text-xs text-muted-foreground">
                  With {formData?.cashbackRate}% cashback and ${formData?.rewardCap} cap, your budget of ${formData?.budget || '0'} can support approximately{' '}
                  <span className="font-semibold text-primary data-text">
                    {formData?.budget ? Math.floor(parseFloat(formData?.budget) / formData?.rewardCap) : 0}
                  </span>{' '}
                  maximum reward transactions.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              fullWidth
            >
              Create Campaign
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;